import { Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminSession } from '@/app/providers/session-provider';
import { useRuntime } from '@/app/providers/runtime-provider';
import { BackofficeLayout } from '@/app/shell/backoffice-layout';
import { AppToaster } from '@/shared/components/app-toaster';
import { SessionSkeleton } from '@/shared/components/session-skeleton';
import { PendingActionReplay } from './pending-action-replay';
import { SessionUnavailable } from './session-unavailable';
import { StepUpController } from './step-up-controller';

/**
 * Layout of every route that needs a back-office session: shows the page only for a signed-in
 * admin. Anonymous visitors are sent to the server-handled sign-in (returnTo = the current
 * path); while the session loads a skeleton is shown; when /platform/me cannot be reached a
 * plain-language retry state is shown.
 */
export function AuthGate() {
  const { t } = useTranslation('backoffice');
  const { redirectToLogin, sessionStore } = useRuntime();
  const { status } = useAdminSession();

  useEffect(() => {
    if (status === 'anonymous') redirectToLogin();
    if (status === 'idle') void sessionStore.bootstrap();
  }, [status, redirectToLogin, sessionStore]);

  if (status === 'authenticated') {
    return (
      <>
        <BackofficeLayout>
          <Outlet />
        </BackofficeLayout>
        <AppToaster />
        <StepUpController />
        <PendingActionReplay />
      </>
    );
  }
  if (status === 'error') return <SessionUnavailable />;
  return (
    <SessionSkeleton
      label={status === 'anonymous' ? t('session.redirecting') : t('session.loading')}
    />
  );
}
